export interface Crumb {
  href?: string;
  title: string;
}

// `satisfies` (not a `Crumb` annotation) keeps `href` non-optional, so
// consumers can use `HOME_CRUMB.href` as a guaranteed-string fallback.
export const HOME_CRUMB = { title: 'Home', href: '/patients' } as const satisfies Crumb;

/**
 * Adapt a route-defined breadcrumb trail for the personal-edition header.
 *
 * Resolution order:
 *  1. A page-provided trail (`pageTrail`) wins verbatim — pages with dynamic
 *     parents (e.g. encounter detail → member) build their own.
 *  2. Otherwise the route's static trail is adapted:
 *     - `Home` is prepended when the trail doesn't start at `/patients`.
 *     - The `/patients` root crumb is retitled ("Patients" → "Home").
 *     - The last crumb's title is replaced with the live page-header title
 *       when one is set (member names, document names, …).
 */
export function buildPersonalTrail(
  routeTrail: Crumb[] | undefined,
  headerTitle?: string,
  pageTrail?: Crumb[],
): Crumb[] {
  if (pageTrail && pageTrail.length > 0) return pageTrail;

  const source = routeTrail ?? [];
  if (source.length === 0) {
    return headerTitle ? [HOME_CRUMB, { title: headerTitle }] : [HOME_CRUMB];
  }

  const trail =
    source[0].href === HOME_CRUMB.href
      ? [{ ...source[0], title: HOME_CRUMB.title }, ...source.slice(1)]
      : [HOME_CRUMB, ...source];

  if (headerTitle) {
    const last = trail.length - 1;
    trail[last] = { ...trail[last], title: headerTitle };
  }
  // The home page collapses to [Home] once the /patients crumb is retitled
  // and the header title ("Home") is applied — dedupe is implicit.
  return trail;
}

/**
 * Where the mobile back arrow should navigate: the crumb just above the
 * current page. `undefined` means "already at the root — hide the arrow".
 * A parent crumb without an href falls back to Home.
 */
export function parentHref(trail: Crumb[]): string | undefined {
  if (trail.length < 2) return undefined;
  return trail[trail.length - 2].href ?? HOME_CRUMB.href;
}
